import { Outlet, useLocation } from "react-router-dom";
import { useLenis } from "@/hooks/useLenis";
import { useScrollDepthTracking } from "@/hooks/useScrollDepthTracking";
import { SketchDefs } from "@/components/Notebook";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { StickyBookCTA } from "@/components/StickyBookCTA";
import { StickyWhatsApp } from "@/components/StickyWhatsApp";
import { ConsentBanner } from "@/components/ConsentBanner";
import { CompactHeader } from "@/components/booking/CompactHeader";
import { CompactFooter } from "@/components/booking/CompactFooter";
import { ROUTES } from "@/config/links";

// The entire /book* booking flow (general selection + 4 dedicated pages)
// gets a compact header/footer and no sticky booking CTA — the visitor is
// already inside the booking journey. Matches against the canonical
// (language-stripped) pathname so /pt/book and /el/book/presentation etc.
// are included too.
const isBookingFlowPath = (pathname) => {
  const local = pathname.replace(/^\/(pt|el)(?=\/|$)/, "") || "/";
  return local === ROUTES.booking || local.startsWith(`${ROUTES.booking}/`);
};

// The Workplace Communication Research page keeps the full site header/
// nav (per spec: "retain the existing site header and navigation") but
// never renders the footer or the sticky booking CTA — a page-specific
// rule, not a global footer removal. The page's own in-content "Book a
// Conversation" CTA (its completion-screen modal) is the only booking
// entry point here, by design, so there is no redundant floating CTA
// over the full-screen questionnaire.
const isResearchPath = (pathname) => {
  const local = pathname.replace(/^\/(pt|el)(?=\/|$)/, "") || "/";
  return local === ROUTES.workplaceCommunicationResearch;
};

/**
 * Shared site chrome: smooth-scroll, notebook filter defs, top nav,
 * routed page outlet, footer, sticky CTAs (Book + WhatsApp), and
 * LGPD consent banner. Swaps to a compact header/footer (and drops the
 * sticky booking CTA) while inside the /book* booking flow.
 */
export const SiteLayout = () => {
  useLenis();
  useScrollDepthTracking();
  const { pathname } = useLocation();
  const isBookingFlow = isBookingFlowPath(pathname);
  const isResearch = isResearchPath(pathname);

  return (
    <div className="paper-grain relative min-h-screen">
      <SketchDefs />
      {isBookingFlow ? <CompactHeader /> : <Nav />}
      <main>
        <Outlet />
      </main>
      {isBookingFlow ? <CompactFooter /> : !isResearch && <Footer />}
      {!isBookingFlow && !isResearch && <StickyBookCTA />}
      <StickyWhatsApp />
      <ConsentBanner />
    </div>
  );
};

export default SiteLayout;
